import { ref, onMounted } from 'vue'
import { useTestExecutionsService } from '@/services/test-executions'
import type { GetEnvironmentInfoResponse } from '@/services/test-executions'

/**
 * Composable to fetch and cache environment info (deployed version)
 * Fetches once on first use and caches the result
 */
const environmentInfo = ref<GetEnvironmentInfoResponse | null>(null)
const isLoading = ref(false)
const error = ref<string | null>(null)
let fetchPromise: Promise<void> | null = null

export function useEnvironmentInfo() {
  const { getEnvironmentInfo } = useTestExecutionsService()

  const fetchEnvironmentInfo = async () => {
    // Already have data
    if (environmentInfo.value) return

    // Already fetching
    if (fetchPromise) {
      await fetchPromise
      return
    }

    isLoading.value = true
    error.value = null

    fetchPromise = (async () => {
      try {
        environmentInfo.value = await getEnvironmentInfo()
      } catch (e) {
        console.warn('Failed to fetch environment info:', e)
        error.value = 'Failed to load'
      } finally {
        isLoading.value = false
        fetchPromise = null
      }
    })()

    await fetchPromise
  }

  // Auto-fetch on first use
  onMounted(() => {
    fetchEnvironmentInfo()
  })

  return {
    environmentInfo,
    isLoading,
    error,
    fetchEnvironmentInfo
  }
}
